import { LoginCredentials, LoginResponse, AdminDashboardData, SubmittedSurvey, SubmittedReport, PaginatedResponse, SurveyFilters, ReportFilters, User, CreateUserRequest, UpdateUserRequest, UserFilters, VerificationStatus, ResendVerificationResponse } from '../types/admin';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Admin authentication endpoints
export const adminLogin = async (credentials: LoginCredentials): Promise<LoginResponse> => {
	const response = await fetch(`${API_BASE_URL}/admin/login`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(credentials),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno prijavljivanje');
	}

	return response.json();
};

export const verifyToken = async (token: string): Promise<{ valid: boolean; user?: any }> => {
	const response = await fetch(`${API_BASE_URL}/admin/verify`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		return { valid: false };
	}

	const data = await response.json();
	return { valid: true, user: data.user };
};

// Dashboard data
export const getDashboardData = async (token: string): Promise<AdminDashboardData> => {
	const response = await fetch(`${API_BASE_URL}/admin/dashboard`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje dashboard podataka');
	}

	return response.json();
};

// Survey management
export const getSurveys = async (token: string, page: number = 1, pageSize: number = 20, filters?: SurveyFilters): Promise<PaginatedResponse<SubmittedSurvey>> => {
	const params = new URLSearchParams({
		page: page.toString(),
		pageSize: pageSize.toString(),
		...(filters && Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== undefined && value !== ''))),
	});

	const response = await fetch(`${API_BASE_URL}/admin/surveys?${params}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje anketa');
	}

	return response.json();
};

export const getSurveyById = async (token: string, surveyId: string): Promise<SubmittedSurvey> => {
	const response = await fetch(`${API_BASE_URL}/admin/surveys/${surveyId}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje ankete');
	}

	return response.json();
};

// Report management
export const getReports = async (token: string, page: number = 1, pageSize: number = 20, filters?: ReportFilters): Promise<PaginatedResponse<SubmittedReport>> => {
	const params = new URLSearchParams({
		page: page.toString(),
		pageSize: pageSize.toString(),
		...(filters && Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== undefined && value !== ''))),
	});

	const response = await fetch(`${API_BASE_URL}/admin/reports?${params}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje izveštaja');
	}

	return response.json();
};

export const getReportById = async (token: string, reportId: string): Promise<SubmittedReport> => {
	const response = await fetch(`${API_BASE_URL}/admin/reports/${reportId}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje izveštaja');
	}

	return response.json();
};

export const updateReportStatus = async (token: string, reportId: string, status: 'pending' | 'reviewed' | 'processed', notes?: string): Promise<SubmittedReport> => {
	const response = await fetch(`${API_BASE_URL}/admin/reports/${reportId}/status`, {
		method: 'PATCH',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ status, notes }),
	});

	if (!response.ok) {
		throw new Error('Neuspešno ažuriranje statusa izveštaja');
	}

	return response.json();
};

// Export survey data (for analysis/backup)
export const exportSurveys = async (token: string, format: 'csv' | 'json' = 'csv', filters?: SurveyFilters): Promise<Blob> => {
	const params = new URLSearchParams({
		format,
		...(filters && Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== undefined && value !== ''))),
	});

	const response = await fetch(`${API_BASE_URL}/admin/surveys/export?${params}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		throw new Error('Neuspešan izvoz anketa');
	}

	return response.blob();
};

// User management endpoints
export const createUser = async (token: string, userData: CreateUserRequest): Promise<User> => {
	const response = await fetch(`${API_BASE_URL}/admin/users`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(userData),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno kreiranje korisnika');
	}

	return response.json();
};

export const getUsers = async (token: string, page: number = 1, pageSize: number = 20, filters?: UserFilters): Promise<PaginatedResponse<User>> => {
	const params = new URLSearchParams({
		page: page.toString(),
		pageSize: pageSize.toString(),
		...(filters && Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== undefined && value !== ''))),
	});

	const response = await fetch(`${API_BASE_URL}/admin/users?${params}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje korisnika');
	}

	return response.json();
};

export const getUserById = async (token: string, userId: string): Promise<User> => {
	const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		throw new Error('Neuspešno dohvatanje korisnika');
	}

	return response.json();
};

export const updateUser = async (token: string, userId: string, userData: UpdateUserRequest): Promise<User> => {
	const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
		method: 'PUT',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(userData),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno ažuriranje korisnika');
	}

	return response.json();
};

export const deleteUser = async (token: string, userId: string): Promise<void> => {
	const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
		method: 'DELETE',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno brisanje korisnika');
	}
};

export const toggleUserStatus = async (token: string, userId: string): Promise<User> => {
	const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/toggle-status`, {
		method: 'PATCH',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešna promena statusa korisnika');
	}

	return response.json();
};

// User verification endpoints
export const getVerificationStatus = async (token: string, userId: string): Promise<VerificationStatus> => {
	const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/verification-status`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno dohvatanje statusa verifikacije');
	}

	return response.json();
};

export const resendVerification = async (token: string, userId: string): Promise<ResendVerificationResponse> => {
	const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/resend-verification`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || 'Neuspešno ponovno slanje verifikacije');
	}

	return response.json();
};
